import { useState, useEffect } from "react";
import { UncontrolledTooltip } from "reactstrap";

import procoreWhiteLogo from "Images/Procore_White_Logo.png";
import procoreBlackLogo from "Images/Procore_Black_Logo.png";
import netsuiteLogo from "Images/Login/Netsuite_Logo.png";
import pmwebLogo from "Images/Login/Pmweb_Logo.png";
import jdeLogo from "Images/Login/Jde_Logo.png";
import peoplesoftLogo from "Images/Login/Peoplesoft_Logo.png";
import sageIntacctLogo from "Images/Login/SageIntacct_Logo.png";
import mriLogo from "Images/Login/Mri_Logo.png";
import cmicLogo from "Images/Login/Cmic_Logo.png";
import coinsLogo from "Images/Login/Coins_Logo.png";
import sapLogo from "Images/Login/Sap_Logo.png";
import northspyreLogo from "Images/Login/Northspyre_Logo.png";
import nexusLogo from "Images/Login/Nexus_Logo.png";

import dimensionSwirl from "Images/Login/dimension_swirl_clean.svg";

import ResetPassword from "Pages/Login/ResetPassword";
import styles from "Pages/Login/Login.module.css";
import LoginForm from "./LoginForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPause } from "@fortawesome/free-solid-svg-icons";
import useTheme from "Hooks/useTheme";

export interface IStatusMessage {
    expires_at: string;
    id: string;
    issued_at: string;
    issued_by: string;
    status_message: string;
    status_type: string;
}

export interface IData {
    oauth_url: string;
}

// Must have at least one lowercase letter, one uppercase letter, one digit,
// and a total length of at least 8 characters
export const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

/** Note that this component is wrapped in Autologin in App / AppRoutes, so that an autologin
 * will be performed if the user has a valid JSON Web Token (JWT) saved as a cookie.
 */
export default function Login() {
    const [whichModalShown, setWhichModalShown] = useState<whichModalShown>("");
    const [pageData, setPageData] = useState<IData>();
    const [isBannerPaused, setIsBannerPaused] = useState(false);
    const { theme } = useTheme();
    const procoreLogo = theme === "dark" ? procoreWhiteLogo : procoreBlackLogo;

    // Get system status messages and procore oauth url
    useEffect(() => {
        let ignore = false;

        getProcoreOauthUrl();

        async function getProcoreOauthUrl() {
            const response = await fetch("/api/login");
            let data: IData | Response;
            try {
                data = await response.json();
            } catch {
                data = response;
            }
            console.log(data);

            if (response.ok && !ignore) {
                setPageData(data as IData);
            }
        }
        return () => {
            ignore = true;
        };
    }, []);

    function onSetWhichModalShown(modalName: whichModalShown) {
        setWhichModalShown(modalName);
    }

    const toggleBannerScroll = () => {
        setIsBannerPaused((prev) => !prev);
    };

    const images = [
        {
            src: procoreLogo,
            alt: "Procore logo",
            url: "https://www.procore.com/",
        },
        { src: cmicLogo, alt: "CMiC logo" },
        { src: netsuiteLogo, alt: "NetSuite logo" },
        { src: mriLogo, alt: "MRI logo" },
        { src: peoplesoftLogo, alt: "PeopleSoft logo" },
        { src: jdeLogo, alt: "JDE logo" },
        { src: pmwebLogo, alt: "PMWeb logo" },
        { src: sageIntacctLogo, alt: "Sage Intacct logo" },
        { src: coinsLogo, alt: "COINS logo" },
        { src: sapLogo, alt: "SAP logo" },
        { src: northspyreLogo, alt: "Northspyre logo" },
        { src: nexusLogo, alt: "Nexus logo" },
    ];

    const loginFormProps = {
        pageData,
        onSetWhichModalShown,
    };

    return (
        <>
            {/* Hero Section */}
            <section className={styles.hero}>
                <div className={styles.heroCenter}>
                    <img
                        className={styles.swirlLogo}
                        src={dimensionSwirl}
                        alt="Dimension logo"
                    />
                    <h1 className={styles.header}>
                        Welcome to Dimension
                    </h1>
                    <p className={styles.subtitle}>
                        Seamlessly connect your project management and
                        accounting systems in near real time
                    </p>
                    <div className={styles.formCard}>
                        <LoginForm {...loginFormProps} />
                    </div>
                </div>
            </section>

            {/* Scrolling banner of system icons */}
            <aside className={styles.bannerSection}>
                <button
                    id="pauseButton"
                    className={`invisibleButton ${styles.pauseButton}`}
                    type="button"
                    onClick={toggleBannerScroll}
                    aria-label="Pause scrolling"
                >
                    <FontAwesomeIcon
                        icon={faPause}
                        className={styles.pauseIcon}
                    />
                </button>
                <UncontrolledTooltip target="pauseButton">
                    {isBannerPaused
                        ? "Unpause scrolling"
                        : "Pause scrolling"}
                </UncontrolledTooltip>
                <div className={styles.scrollBanner}>
                    <div
                        className={
                            isBannerPaused
                                ? `${styles.scrollBannerContent} ${styles.paused}`
                                : styles.scrollBannerContent
                        }
                    >
                        {images.map((image) => (
                            <BrandIcon
                                key={image.alt.replace(" ", "")}
                                src={image.src}
                                alt={image.alt}
                            />
                        ))}
                        {/* Two sets so we can have infinite scrolling */}
                        {images.map((image) => (
                            <BrandIcon
                                key={image.alt.replace(" ", "")}
                                src={image.src}
                                alt={image.alt}
                            />
                        ))}
                    </div>
                </div>
            </aside>

            {/* Footer */}
            <footer className={styles.contactRow}>
                <p className={styles.contactText}>
                    Contact{" "}
                    <a
                        className={styles.headerLink}
                        href="https://www.calanceus.com/services/project-control-integrations"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Calance
                    </a>{" "}
                    for more information and to get in touch
                </p>
            </footer>

            {/* Modals */}
            <ResetPassword
                whichModalShown={whichModalShown}
                onSetWhichModalShown={onSetWhichModalShown}
            />
        </>
    );
}

function BrandIcon({
    src,
    alt,
    url,
}: {
    src: string;
    alt: string;
    url?: string;
}) {
    return (
        <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className={styles.systemIconLink}
        >
            <img src={src} alt={alt} className={styles.systemIcon} />
        </a>
    );
}
